'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import type { MediaRecord, PhotoTypeConfig } from './types';

interface WorkspaceHeaderProps {
  photoTypes: PhotoTypeConfig[];
  photos: MediaRecord[];
  videos: MediaRecord[];
  aiVisible: boolean;
  // Denser layout (smaller ring, 2-column stat grid) for narrow viewports —
  // the default flex-wrap row stacks each stat onto its own line under
  // ~500px, making the header too tall to keep sticky above a scrolling list.
  compact?: boolean;
}

const RING_RADIUS = 22;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function StatBlock({ label, value, compact }: { label: string; value: React.ReactNode; compact?: boolean }) {
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-0.5">
      <span className={cn('font-semibold text-neutral-900 dark:text-neutral-100', compact ? 'text-base' : 'text-xl')}>
        {value}
      </span>
      <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>
    </div>
  );
}

export function WorkspaceHeader({ photoTypes, photos, videos, aiVisible, compact = false }: WorkspaceHeaderProps) {
  const requiredTypes = photoTypes.filter((type) => type.required);
  const filledRequired = requiredTypes.filter((type) =>
    photos.some((photo) => photo.photo_type === type.code)
  ).length;
  const pendingRequired = requiredTypes.length - filledRequired;
  const percentage =
    requiredTypes.length === 0 ? 100 : Math.round((filledRequired / requiredTypes.length) * 100);

  const estimatedCost = [...photos, ...videos]
    .filter((record) => record.detected_damage?.estimated_total_cost)
    .reduce((sum, record) => sum + record.detected_damage!.estimated_total_cost!.recommended_total, 0);
  const hasCost = aiVisible && estimatedCost > 0;

  const dashOffset = RING_CIRCUMFERENCE * (1 - percentage / 100);

  const ringSize = compact ? 'h-10 w-10' : 'h-14 w-14';

  return (
    <div
      className={cn(
        'flex items-center rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900',
        compact ? 'gap-3 px-3 py-2.5' : 'gap-8 px-6 py-4'
      )}
    >
      <div
        role="progressbar"
        aria-label="Required slots completed"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn('relative flex flex-shrink-0 items-center justify-center', ringSize)}
      >
        <svg viewBox="0 0 56 56" className={cn('-rotate-90', ringSize)}>
          <circle cx="28" cy="28" r={RING_RADIUS} className="fill-none stroke-neutral-200 dark:stroke-neutral-700" strokeWidth="5" />
          <circle
            cx="28"
            cy="28"
            r={RING_RADIUS}
            className="fill-none stroke-primary-500 transition-[stroke-dashoffset] duration-300 ease-in-out"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <span className={cn('absolute font-semibold text-neutral-900 dark:text-neutral-100', compact ? 'text-[10px]' : 'text-xs')}>
          {percentage}%
        </span>
      </div>

      <div className={cn('flex flex-1 items-center', compact ? 'grid grid-cols-2 gap-x-4 gap-y-1' : 'flex-wrap gap-8')}>
        <StatBlock label="Photos uploaded" value={photos.length} compact={compact} />
        <StatBlock label="Videos uploaded" value={videos.length} compact={compact} />
        <StatBlock label="Pending required slots" value={pendingRequired} compact={compact} />
        {hasCost && <StatBlock label="Estimated cost" value={`₹${estimatedCost.toLocaleString('en-IN')}`} compact={compact} />}
      </div>
    </div>
  );
}
